import {
  both,
  checkShield,
  club,
  market,
  minusShield,
} from "@/designSystemIcons";
import {
  initialState,
  removeAllExcludedPlayers,
  removeAllIncludedPlayers,
  updateExcludeActiveSquad,
  updateExcludeObjectivePlayers,
  updateExcludeSbcPlayers,
  updateIncludePlayersFrom,
  updateIncludeTransferlist,
  updateMaxRating,
  updateMinRating,
  updatePrioritizeDuplicates,
  updatePrioritizeSbcStorage,
  updateUseUntradeablesOnly,
} from "@/redux/sbcs/sbcFiltersSlice";
import { AppDispatch } from "@/redux/store";

export interface RawFilterItem {
  condition: boolean;
  alt: any;
  icon?: any;
  imageSrc?: string;
  onClick?: () => void;
}

type FilterConfigEntry = {
  key: keyof typeof initialState;
  alt: string;
  icon: React.ReactNode;
  onClear?: () => any;
};

const FILTER_CONFIG: FilterConfigEntry[] = [
  {
    key: "prioritizeDuplicates",
    alt: "Duplicate",
    icon: (
      <img
        src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/duplicate.png`}
        alt="duplicate"
      />
    ),
    onClear: () => updatePrioritizeDuplicates(false),
  },
  {
    key: "includeTransferlist",
    alt: "Transferlist",
    icon: (
      <img
        src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/transfer-list.png`}
        alt="transfer list"
      />
    ),
    onClear: () => updateIncludeTransferlist(false),
  },
  {
    key: "prioritizeSbcStorage",
    alt: "SBC storage",
    icon: (
      <img
        src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/sbc-storage.png`}
        alt="sbc storage"
      />
    ),
    onClear: () => updatePrioritizeSbcStorage(false),
  },
  {
    key: "useUntradeablesOnly",
    alt: "Untradeable",
    icon: (
      <img
        src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/untradeable.png`}
        alt="untradeable"
      />
    ),
    onClear: () => updateUseUntradeablesOnly(false),
  },
  {
    key: "excludeActiveSquad",
    alt: "Active squad",
    icon: (
      <img
        src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/active-squad.png`}
        alt="active squad"
      />
    ),
    onClear: () => updateExcludeActiveSquad(false),
  },
  {
    key: "excludeSbcPlayers",
    alt: "SBC",
    icon: (
      <img
        src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/sbc-player.png`}
        alt="sbc players"
      />
    ),
    onClear: () => updateExcludeSbcPlayers(false),
  },
  {
    key: "excludeObjectivePlayers",
    alt: "Objective",
    icon: (
      <img
        src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/season-point.png`}
        alt="objective"
      />
    ),
    onClear: () => updateExcludeObjectivePlayers(false),
  },
];

export const buildRawFilterItems = (
  state: typeof initialState,
  dispatch: AppDispatch,
): RawFilterItem[] => {
  const {
    playersFrom,
    includedPlayers,
    excludedPlayers,
    minRating,
    maxRating,
  } = state;

  const items: RawFilterItem[] = [];

  items.push({
    condition: true,
    alt:
      playersFrom === "club"
        ? "club"
        : playersFrom === "both"
          ? "both"
          : "market",
    icon: () =>
      playersFrom === "both"
        ? both(true)
        : playersFrom === "club"
          ? club(true)
          : market(true),
    onClick: () => dispatch(updateIncludePlayersFrom("market")),
  });

  items.push(
    ...FILTER_CONFIG.filter(({ key }) => state[key]).map(
      ({ alt, icon, onClear }) => ({
        condition: true,
        alt,
        icon,
        onClick: onClear ? () => dispatch(onClear()) : undefined,
      }),
    ),
  );

  if (minRating !== null || maxRating !== null) {
    items.push({
      condition: true,
      alt: "Rating Range",
      icon: () => `Rating ${minRating ?? 0}-${maxRating ?? 99}`,
      onClick: () => {
        dispatch(updateMinRating(null));
        dispatch(updateMaxRating(null));
      },
    });
  }

  if (includedPlayers.length > 0) {
    items.push({
      condition: true,
      alt: "Included Players",
      icon: () => [checkShield, includedPlayers.length],
      onClick: () => dispatch(removeAllIncludedPlayers()),
    });
  }

  if (excludedPlayers.length > 0) {
    items.push({
      condition: true,
      alt: "Excluded Players",
      icon: () => [minusShield, excludedPlayers.length],
      onClick: () => dispatch(removeAllExcludedPlayers()),
    });
  }

  return items;
};
